import { type ClientContext, ContextClient } from '~/services/contextClient';
import WorkflowsClientFactory from './workflowsClient.factory';
import type {
    CreateWorkflowComment,
    CreateWorkflow,
    ListWorkflowsParams,
    UpdateDraft,
    UpdateWorkflow,
    UpdateWorkflowComment,
    WorkflowComment,
    WorkflowsClientInterface,
} from './workflowsClient.types';
import type { Workflow, DrainingVersion } from '~/types/workflow.types';
import type { ExtendedAxiosRequestConfig } from '../BaseClient';
import type { AxiosPromise } from 'axios';

class WorkflowsClient extends ContextClient<WorkflowsClientInterface> {
    constructor() {
        super(WorkflowsClientFactory);
    }

    getWorkflows(
        context: ClientContext,
        params?: ListWorkflowsParams,
        extraConfig?: ExtendedAxiosRequestConfig
    ): AxiosPromise<Workflow[]> {
        return this.getClient(context).getWorkflows(params, extraConfig);
    }

    getWorkflow(context: ClientContext, workflowId: string, version?: number): AxiosPromise<Workflow> {
        return this.getClient(context).getWorkflow(workflowId, version);
    }

    getDependentWorkflows(context: ClientContext, workflowId: string): AxiosPromise<Workflow[]> {
        return this.getClient(context).getDependentWorkflows(workflowId);
    }

    createWorkflow(context: ClientContext, input: CreateWorkflow): AxiosPromise<Workflow> {
        return this.getClient(context).createWorkflow(input);
    }

    updateWorkflow(context: ClientContext, workflowId: string, input: UpdateWorkflow): AxiosPromise<Workflow> {
        return this.getClient(context).updateWorkflow(workflowId, input);
    }

    createDraft(context: ClientContext, workflowId: string): AxiosPromise<Workflow> {
        return this.getClient(context).createDraft(workflowId);
    }

    updateDraft(context: ClientContext, workflowId: string, input: UpdateDraft): AxiosPromise<Workflow> {
        return this.getClient(context).updateDraft(workflowId, input);
    }

    publishWorkflow(context: ClientContext, workflowId: string): AxiosPromise<Workflow> {
        return this.getClient(context).publishWorkflow(workflowId);
    }

    archiveWorkflow(context: ClientContext, workflowId: string): AxiosPromise<void> {
        return this.getClient(context).archiveWorkflow(workflowId);
    }

    restoreWorkflow(context: ClientContext, workflowId: string): AxiosPromise<void> {
        return this.getClient(context).restoreWorkflow(workflowId);
    }

    getWorkflowVersions(context: ClientContext, workflowId: string): AxiosPromise<Workflow[]> {
        return this.getClient(context).getWorkflowVersions(workflowId);
    }

    getDrainingVersions(context: ClientContext, workflowId: string): AxiosPromise<DrainingVersion[]> {
        return this.getClient(context).getDrainingVersions(workflowId);
    }

    getWorkflowComments(context: ClientContext): AxiosPromise<WorkflowComment[]> {
        return this.getClient(context).getWorkflowComments();
    }

    createWorkflowComment(context: ClientContext, input: CreateWorkflowComment): AxiosPromise<WorkflowComment> {
        return this.getClient(context).createWorkflowComment(input);
    }

    updateWorkflowComment(
        context: ClientContext,
        commentId: string,
        input: UpdateWorkflowComment
    ): AxiosPromise<WorkflowComment> {
        return this.getClient(context).updateWorkflowComment(commentId, input);
    }

    deleteWorkflowComment(context: ClientContext, commentId: string): AxiosPromise<void> {
        return this.getClient(context).deleteWorkflowComment(commentId);
    }
}

export default new WorkflowsClient();
